import { pxToEm } from 'util/pxToEm';
import { Box, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import { Control, FieldErrors, UseFormSetValue, UseFormHandleSubmit } from 'react-hook-form';
import { Button } from 'components/ui';
import { FeedbackFormData } from '../constants';
import { RatingSection } from '../RatingSection';
import { QuestionsSection } from '../QuestionsSection';
import { CommentsSection } from '../CommentsSection';

export interface FeedbackFormProps {
  questions: Array<{ id: string; question: string }>;
  selectedQuestionIds: string[];
  rating: number;
  control: Control<FeedbackFormData>;
  setValue: UseFormSetValue<FeedbackFormData>;
  handleSubmit: UseFormHandleSubmit<FeedbackFormData>;
  errors: FieldErrors<FeedbackFormData>;
  isSubmitting: boolean;
  onSubmit: (data: FeedbackFormData) => void;
  readOnly?: boolean;
}

export const FeedbackForm = ({
  questions,
  selectedQuestionIds,
  rating,
  control,
  setValue,
  handleSubmit,
  errors,
  isSubmitting,
  onSubmit,
  readOnly = false,
}: FeedbackFormProps): ReactElement => {
  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
        },
      }}
    >
      <form onSubmit={readOnly ? undefined : handleSubmit(onSubmit)}>
        <RatingSection control={control} errors={errors} readOnly={readOnly} />

        <QuestionsSection
          questions={questions}
          selectedQuestionIds={selectedQuestionIds}
          control={control}
          setValue={setValue}
          errors={errors}
          readOnly={readOnly}
        />

        <CommentsSection control={control} readOnly={readOnly} />

        {/* Submit Button - only show if not read-only */}
        {!readOnly && (
          <Box display="flex" flexDirection="column" alignItems="center" gap={16} mb={40}>
            <Button
              type="submit"
              variant="vibrant"
              sx={{
                minWidth: 320,
                minHeight: 48,
                fontSize: pxToEm(14),
                fontWeight: 400,
                backgroundColor: theme => theme.palette.vibrant.primary,
                '&:hover': {
                  backgroundColor: theme => theme.palette.vibrant.primary,
                  opacity: 0.9,
                },
              }}
              disabled={isSubmitting || rating === 0}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
            <Typography
              sx={{
                fontSize: pxToEm(12),
                fontWeight: 500,
                color: '#383838',
                textAlign: 'center',
              }}
            >
              Your feedback helps us improve campus events.
            </Typography>
          </Box>
        )}
      </form>
    </Box>
  );
};

